import { Result } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import CustomTable from "components/CustomTable";
import PageHeader from "components/PageHeader";
import { PrimaryButtonOutLine } from "components/PrimaryButton";
import {
  ProgramConfirmModal,
  ProgramFormModal,
  useProgramFormModal,
} from "features/LoyaltyPackage/presentation/pages/ProgramFormModal";
import {
  LoyaltyDetailEditButton,
  LoyaltyDetailMetaDivider,
  LoyaltyDetailMetaField,
  LoyaltyDetailMetaRow,
  LoyaltyDetailSummary,
  LoyaltySection,
  LoyaltyStatusBadge,
  LoyaltyTableActions,
} from "features/LoyaltyPackage/presentation/components";
import { LOYALTY_PACKAGE_STATUS } from "features/LoyaltyPackage/core/constants";
import { useLoyaltyPackage, usePeriodCreatedByNameMap } from "features/LoyaltyPackage/data/hooks";
import {
  formatLoyaltyDateRangeMultiline,
  formatLoyaltyProgramSummaryDateRange,
  getLoyaltyApplyScopeLabel,
  getLoyaltyCreatedByDisplay,
  getLoyaltyTimeIntervalLabel,
} from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import useProgramDetail from "./useProgramDetail";

import "./index.scss";

function ProgramDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { TEXT_STATUS_LOYALTY } = useLoyaltyPackage();

  const {
    program,
    programLoading,
    periods,
    periodsLoading,
    refetchProgram,
    switchLoading,
    handleSwitchPeriodStatus,
    isPeriodEditable,
  } = useProgramDetail(id);

  const createdByNameById = usePeriodCreatedByNameMap(periods);

  const handleToggleConfirm = async (record) => {
    try {
      await handleSwitchPeriodStatus(record, !record.isActive);
      notify.success({ message: t("customer.updateStatusSuccess") });
    } catch (err) {
      notify.error({
        message: err?.message || t("loyaltyPackage.package.updateFailed"),
      });
    }
  };

  const programFormModal = useProgramFormModal({
    programId: id,
    afterSubmitRefetch: "detail",
    refetch: refetchProgram,
  });

  const isEditable = program?.status === LOYALTY_PACKAGE_STATUS.UPCOMING;
  const canAddPackage = [LOYALTY_PACKAGE_STATUS.UPCOMING, LOYALTY_PACKAGE_STATUS.ONGOING].includes(program?.status);

  const columns = [
    {
      title: t("common.index"),
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: t("loyaltyPackage.package.name"),
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <Link to={`/loyalty-package/package/${record.id}`} className="program-detail-package-link">
          {title}
        </Link>
      ),
    },
    {
      title: t("loyaltyPackage.package.timeIntervalType"),
      dataIndex: "timeInterval",
      key: "timeInterval",
      width: 160,
      render: (timeInterval) => getLoyaltyTimeIntervalLabel(timeInterval),
    },
    {
      title: t("loyaltyPackage.package.timePeriod"),
      key: "timePeriod",
      width: 220,
      render: (_, record) => {
        const range = formatLoyaltyDateRangeMultiline(record.startTime, record.endTime);
        if (typeof range === "string") return range;
        return (
          <span className="program-detail-date-range">
            {range.start} - <br /> {range.end}
          </span>
        );
      },
    },
    {
      title: t("loyaltyPackage.package.applyScope"),
      key: "applyScope",
      width: 150,
      render: (_, record) => getLoyaltyApplyScopeLabel(record.applyScope, record.numOfProducts),
    },
    {
      title: t("loyaltyPackage.package.status"),
      key: "status",
      align: "center",
      width: 150,
      render: (_, record) => TEXT_STATUS_LOYALTY[record.status] ?? record.status,
    },
    {
      title: t("loyaltyPackage.package.createdBy"),
      key: "createdBy",
      render: (_, record) => getLoyaltyCreatedByDisplay(record, createdByNameById),
    },
    {
      title: t("loyaltyPackage.package.actions"),
      key: "actions",
      align: "center",
      fixed: "right",
      width: 180,
      className: "loyalty-action-column",
      render: (_, record) => (
        <LoyaltyTableActions
          isActive={record.isActive}
          switchLoading={switchLoading}
          onToggle={() => handleToggleConfirm(record)}
          confirmOnMessage={t("loyaltyPackage.package.confirmOn")}
          confirmOffMessage={t("loyaltyPackage.package.confirmOff")}
          showClone
          onClone={() => history.push(`/loyalty-package/package/create?clone=${record.id}`)}
          cloneTooltip={t("common.clone")}
          showEdit={isPeriodEditable(record)}
          onEdit={() => history.push(`/loyalty-package/package/edit/${record.id}`)}
          editTooltip={t("common.edit")}
        />
      ),
    },
  ];

  const formInitialValues = programFormModal.pendingFormValues || programFormModal.getInitialFormValues();

  const programDetailRoutes = [
    {
      path: "/loyalty-package",
      name: t("loyaltyPackage.title"),
    },
    {
      path: "",
      name: t("loyaltyPackage.programDetail.title"),
    },
  ];

  if (!programLoading && !program) {
    return (
      <div className="program-detail-container">
        <PageHeader pageTitle={t("loyaltyPackage.programDetail.title")} routes={programDetailRoutes} />
        <Result status="404" title={t("loyaltyPackage.programDetail.notFound")} />
      </div>
    );
  }

  const timeRangeSummary = formatLoyaltyProgramSummaryDateRange(
    program?.startTime,
    program?.endTime,
    t("loyaltyPackage.programForm.from"),
    t("loyaltyPackage.programForm.to")
  );

  return (
    <div className="program-detail-container">
      <PageHeader pageTitle={t("loyaltyPackage.programDetail.title")} routes={programDetailRoutes} />

      <LoyaltyDetailSummary
        loading={programLoading}
        title={program?.title}
        meta={
          <LoyaltyDetailMetaRow>
            {program?.status && <LoyaltyStatusBadge status={program.status} />}
            {program?.status && <LoyaltyDetailMetaDivider />}
            <LoyaltyDetailMetaField label={t("loyaltyPackage.program.timePeriod")}>
              {timeRangeSummary}
            </LoyaltyDetailMetaField>
          </LoyaltyDetailMetaRow>
        }
        action={
          <LoyaltyDetailEditButton
            label={t("loyaltyPackage.programDetail.edit")}
            disabled={!isEditable}
            disabledTooltip={t("loyaltyPackage.programDetail.editDisabled")}
            onClick={() => programFormModal.openEdit(program)}
          />
        }
      />

      <LoyaltySection
        title={t("loyaltyPackage.programDetail.packagesSection")}
        bodyClassName="custom-table-list-loyalty"
        action={
          canAddPackage ? (
            <PrimaryButtonOutLine
              title={t("common.add")}
              icon={<PlusOutlined style={{ fontSize: "13px" }} />}
              style={{
                color: "#1d39c4", // Khớp màu tiêu đề section ($loyalty-section-title-color)
                borderColor: "#1d39c4", // Khớp màu viền
                backgroundColor: "#f0f3ff",
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 12px",
                fontSize: "13px",
                fontWeight: "500",
              }}
              onClick={() => history.push(`/loyalty-package/package/create?programId=${id}`)}
            />
          ) : null
        }
      >
        <CustomTable
          columns={columns}
          dataSource={periods}
          rowKey="id"
          loading={periodsLoading || programLoading}
          scroll={{ x: "max-content" }}
          pagination={false}
        />
      </LoyaltySection>

      <ProgramFormModal
        key={programFormModal.formSessionKey}
        visible={programFormModal.isFormOpen}
        mode={programFormModal.formMode}
        initialValues={formInitialValues}
        onCancel={programFormModal.closeAll}
        onSubmit={programFormModal.submitForm}
      />

      <ProgramConfirmModal
        visible={programFormModal.isConfirmOpen}
        mode={programFormModal.formMode}
        data={programFormModal.pendingFormValues}
        loading={programFormModal.isSubmitting}
        onCancel={programFormModal.backToForm}
        onConfirm={programFormModal.confirmSubmit}
      />
    </div>
  );
}

export default ProgramDetail;
